import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Stack, List, Avatar,
  Skeleton, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicIcon from '@mui/icons-material/Mic';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import PersonIcon from '@mui/icons-material/Person';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MicNoneIcon from '@mui/icons-material/MicNone';
import SettingsIcon from '@mui/icons-material/Settings';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Capacitor } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeTranscription, NativeRecorder } from '../../hooks/useRealtimeTranscription';
import { useVoiceAgent } from '../../hooks/useVoiceAgent';
import PatientDetail from '../PatientDetail';
import AddPatientDialog from '../AddPatientDialog';
// import ImportPatientsDialog from '../ImportPatientsDialog'; // TODO: Re-enable when file exists
import HillShape from '../HillShape';
import OnboardingOverlay, { type OnboardingRefs } from '../OnboardingOverlay';
import RecordingModal from '../RecordingModal';
import { glass } from '../../themes';

/* ── Patients panel ── */

function PatientsPanel({ onSelect, onAdd, onImport, refreshKey }: { onSelect: (id: number) => void; onAdd: () => void; onImport: () => void; refreshKey: number }) {
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const theme = useTheme();

  const load = () => {
    setLoading(true);
    api.getPatients(search || undefined)
      .then(setPatients)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [search, refreshKey]);

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    setTimeout(async () => {
      await api.deletePatient(id);
      setDeletingId(null);
      load();
    }, 300);
  };

  if (loading) {
    return (
      <>
        <Typography sx={{ mb: 2, color: glass.text, fontFamily: '"DM Serif Display", serif', fontSize: '1.5rem' }}>Patients</Typography>
        <Stack spacing={1.5}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={130} sx={{ borderRadius: '16px' }} />
          ))}
        </Stack>
      </>
    );
  }

  if (patients.length === 0) {
    return (
      <>
        <Typography sx={{ mb: 2, color: glass.text, fontFamily: '"DM Serif Display", serif', fontSize: '1.5rem' }}>Patients</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'rgba(30,58,95,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PeopleOutlineIcon sx={{ fontSize: 36, color: 'rgba(30,58,95,0.3)' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: glass.text, fontSize: '1rem' }}>No patients yet</Typography>
          <Typography sx={{ color: glass.textSecondary, fontSize: '0.875rem', textAlign: 'center', maxWidth: 220 }}>
            Add your first patient to start documenting encounters
          </Typography>
          <Box
            onClick={onAdd}
            sx={{
              mt: 1, px: 3, py: 1.25, borderRadius: '14px',
              bgcolor: '#1e3a5f', color: '#fff',
              display: 'flex', alignItems: 'center', gap: 1,
              cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
              boxShadow: '0 4px 16px rgba(30,58,95,0.25)',
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} /> Add Patient
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography sx={{ mb: 2, color: glass.text, fontFamily: '"DM Serif Display", serif', fontSize: '1.5rem' }}>Patients</Typography>
      <Stack spacing={1.5}>
        {patients.map((p) => (
          <Box
            key={p.id}
            onClick={() => onSelect(p.id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 1.5, borderRadius: '16px', cursor: 'pointer',
              bgcolor: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(255,255,255,0.7)',
              backdropFilter: 'blur(12px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease, background 0.15s',
              opacity: deletingId === p.id ? 0 : 1,
              transform: deletingId === p.id ? 'translateX(40px)' : 'translateX(0)',
              pointerEvents: deletingId === p.id ? 'none' : undefined,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.75)' },
            }}
          >
            <Avatar sx={{ width: 44, height: 44, bgcolor: '#1e3a5f', color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>
              {p.first_name[0]}{p.last_name[0]}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: glass.text }}>
                  {p.first_name} {p.last_name}
                </Typography>
                {p.created_at && (
                  <Typography sx={{ fontSize: '0.7rem', color: glass.textSecondary, flexShrink: 0, ml: 1 }}>
                    {new Date(p.created_at + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                )}
              </Stack>
              {p.notes ? (
                <Typography sx={{ fontSize: '0.8rem', color: glass.textSecondary, mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {p.notes}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.2)', mt: 0.25, fontStyle: 'italic' }}>No notes</Typography>
              )}
            </Box>
            <CloseIcon
              onClick={(e) => handleDelete(p.id, e)}
              sx={{ fontSize: 16, color: 'rgba(0,0,0,0.25)', flexShrink: 0, '&:hover': { color: 'rgba(180,0,0,0.6)' } }}
            />
          </Box>
        ))}
      </Stack>
    </>
  );
}

/* ── Recordings panel ── */

function RecordingsPanel({ refreshKey }: { refreshKey: number }) {
  const [recordings, setRecordings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState<any[]>([]);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [assignSearch, setAssignSearch] = useState('');
  const theme = useTheme();

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getRecordings(), api.getPatients()])
      .then(([recs, pats]) => { setRecordings(recs); setPatients(pats); })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const handleDelete = async (id: number) => {
    await api.deleteRecording(id);
    setRecordings(prev => prev.filter(r => r.id !== id));
  };

  const handleAssign = async (recordingId: number, patientId: number | null) => {
    const updated = await api.assignRecording(recordingId, patientId);
    setRecordings(prev => prev.map(r => r.id === recordingId ? updated : r));
    setAssigningId(null);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' at ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Box sx={{ px: 1 }}>
        <Stack spacing={1.5}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={90} sx={{ borderRadius: '14px' }} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (recordings.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'rgba(30,58,95,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MicNoneIcon sx={{ fontSize: 36, color: 'rgba(30,58,95,0.3)' }} />
        </Box>
        <Typography sx={{ fontWeight: 600, color: glass.text, fontSize: '1rem' }}>No recordings yet</Typography>
        <Typography sx={{ color: glass.textSecondary, fontSize: '0.875rem', textAlign: 'center', maxWidth: 220 }}>
          Tap the mic button to record a patient encounter
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, minHeight: '80vh' }}>
      <Stack spacing={1.5}>
        {recordings.map(r => (
          <Box key={r.id} sx={{
            bgcolor: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(255,255,255,0.7)',
            borderRadius: '14px',
            p: 1.5,
            backdropFilter: 'blur(12px)',
          }}>
            <Typography sx={{ fontSize: '15px', color: glass.text, lineHeight: 1.55, mb: 0.75 }}>
              {r.transcript.length > 200 ? r.transcript.slice(0, 200) + '…' : r.transcript}
            </Typography>
            {r.patient_id && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(30,58,95,0.08)', borderRadius: '8px', px: 1, py: 0.25, mb: 0.75 }}>
                <PersonIcon sx={{ fontSize: 14, color: '#1e3a5f' }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#1e3a5f', fontWeight: 600 }}>
                  {r.patient_first_name} {r.patient_last_name}
                </Typography>
                <Typography
                  onClick={() => handleAssign(r.id, null)}
                  sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.3)', cursor: 'pointer', ml: 0.5, '&:hover': { color: 'rgba(0,0,0,0.6)' } }}
                >
                  ✕
                </Typography>
              </Box>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: glass.textSecondary }}>{formatDate(r.created_at)}</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {!r.patient_id && (
                  <Typography
                    variant="caption"
                    onClick={() => { setAssigningId(assigningId === r.id ? null : r.id); setAssignSearch(''); }}
                    sx={{ color: '#1e3a5f', fontWeight: 600, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                  >
                    Assign
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  onClick={() => handleDelete(r.id)}
                  sx={{ color: 'rgba(200,0,0,0.5)', cursor: 'pointer', '&:hover': { color: 'rgba(200,0,0,0.8)' } }}
                >
                  Delete
                </Typography>
              </Stack>
            </Stack>
            {assigningId === r.id && (() => {
              const filtered = patients.filter(p => {
                if (!assignSearch) return true;
                const q = assignSearch.toLowerCase();
                return p.first_name.toLowerCase().includes(q) || p.last_name.toLowerCase().includes(q);
              });
              return (
                <Box sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '10px', p: 1 }}>
                  {patients.length > 10 && (
                    <input
                      autoFocus
                      placeholder="Search patients…"
                      value={assignSearch}
                      onChange={e => setAssignSearch(e.target.value)}
                      style={{
                        width: '100%', border: 'none', outline: 'none', background: 'rgba(0,0,0,0.04)',
                        borderRadius: 6, padding: '6px 8px', fontSize: '0.8rem', marginBottom: 6, boxSizing: 'border-box',
                      }}
                    />
                  )}
                  <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                    {filtered.length === 0 && (
                      <Typography sx={{ fontSize: '0.8rem', color: glass.textSecondary, textAlign: 'center', py: 1 }}>No patients found</Typography>
                    )}
                    {filtered.map(p => (
                      <Box
                        key={p.id}
                        onClick={() => handleAssign(r.id, p.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '8px',
                          cursor: 'pointer', '&:hover': { bgcolor: 'rgba(30,58,95,0.08)' },
                        }}
                      >
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#3bb8b8', color: '#fff' }}>
                          {p.first_name[0]}{p.last_name[0]}
                        </Avatar>
                        <Typography sx={{ fontSize: '0.85rem', color: glass.text }}>
                          {p.first_name} {p.last_name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/* ── Settings panel ── */

function SettingsPanel({ selectedDeviceId, onSelectDevice }: { selectedDeviceId: string; onSelectDevice: (id: string) => void }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const { signOut } = useAuth();
  const theme = useTheme();

  const scan = useCallback(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices.enumerateDevices().then(all => {
      setDevices(all.filter(d => d.kind === 'audioinput'));
    });
  }, []);

  useEffect(() => { scan(); }, [scan]);

  return (
    <Box sx={{ px: 1, minHeight: '80vh' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: glass.textSecondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Audio Input</Typography>
      <Stack spacing={1}>
        <Box
          onClick={() => onSelectDevice('')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)', cursor: 'pointer' }}
        >
          {selectedDeviceId === '' ? <RadioButtonCheckedIcon sx={{ fontSize: 20, color: '#1e3a5f' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.25)' }} />}
          <Typography sx={{ fontSize: '0.9rem', color: glass.text, fontWeight: selectedDeviceId === '' ? 600 : 400 }}>System Default</Typography>
        </Box>
        {devices.map(d => (
          <Box
            key={d.deviceId}
            onClick={() => onSelectDevice(d.deviceId)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)', cursor: 'pointer' }}
          >
            {selectedDeviceId === d.deviceId ? <RadioButtonCheckedIcon sx={{ fontSize: 20, color: '#1e3a5f' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.25)' }} />}
            <Typography sx={{ fontSize: '0.9rem', color: glass.text, fontWeight: selectedDeviceId === d.deviceId ? 600 : 400 }}>{d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`}</Typography>
          </Box>
        ))}
        {devices.length === 0 && (
          <Typography sx={{ color: glass.textSecondary, textAlign: 'center', mt: 2, fontSize: '0.85rem' }}>
            No audio devices found. Grant microphone permission and tap refresh.
          </Typography>
        )}
      </Stack>

      <Box
        onClick={async () => {
          if (Capacitor.isNativePlatform()) {
            await NativeSettings.open({
              optionAndroid: AndroidSettings.Bluetooth,
              optionIOS: IOSSettings.App,
            });
          } else {
            alert('Open your device\'s Bluetooth settings to pair a new microphone, then tap Refresh.');
          }
        }}
        sx={{
          mt: 3, p: 1.5, borderRadius: '12px',
          bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)',
          display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
        }}
      >
        <BluetoothIcon sx={{ fontSize: 20, color: '#1e3a5f' }} />
        <Box>
          <Typography sx={{ fontSize: '0.9rem', color: glass.text, fontWeight: 500 }}>Pair new Bluetooth device</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>Opens Settings to pair a device</Typography>
        </Box>
      </Box>

      <Box
        onClick={signOut}
        sx={{
          mt: 2, p: 1.5, borderRadius: '12px', bgcolor: 'rgba(220,0,0,0.04)',
          border: '1px solid rgba(220,0,0,0.08)',
          display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
        }}
      >
        <Typography sx={{ fontSize: '0.9rem', color: 'rgba(180,0,0,0.7)', fontWeight: 500 }}>Sign Out</Typography>
      </Box>
    </Box>
  );
}

/* ── Main layout ── */

export default function BottomSheetPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [displayedId, setDisplayedId] = useState<number | null>(null);
  const [contentVisible, setContentVisible] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [recordingModalOpen, setRecordingModalOpen] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [recordingsRefreshKey, setRecordingsRefreshKey] = useState(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  const { state: agentState, send: sendToAgent, dismiss: dismissAgent } = useVoiceAgent();

  // Refresh lists when agent finishes
  useEffect(() => {
    if (agentState.status === 'done') {
      setRefreshKey(k => k + 1);
      setRecordingsRefreshKey(k => k + 1);
    }
  }, [agentState.status]);

  const micRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLDivElement>(null);
  const settingsIconRef = useRef<HTMLDivElement>(null);
  const recordingsIconRef = useRef<HTMLDivElement>(null);
  const patientsIconRef = useRef<HTMLDivElement>(null);

  const transcriptRef = useRef('');
  const savingRef = useRef(false);
  const onCommit = useCallback((text: string) => {
    transcriptRef.current = transcriptRef.current ? transcriptRef.current + ' ' + text : text;
  }, []);
  const { isRecording, partialText, startTranscription, stopTranscription, setRemoteToggle } = useRealtimeTranscription({
    onCommit,
    onSilence: () => { if (!savingRef.current) handleMicToggle(); },
    deviceId: selectedDeviceId || undefined,
  });
  const handleMicToggle = async () => {
    if (savingRef.current) return;
    if (isRecording) {
      savingRef.current = true;
      try {
        await stopTranscription();
        const finalTranscript = transcriptRef.current.trim();
        console.log('[Recording] final transcript:', JSON.stringify(finalTranscript));
        if (finalTranscript) {
          sendToAgent(finalTranscript);
          // Keep modal open — agent response will show inside it
        } else {
          setRecordingModalOpen(false);
        }
      } catch (e) {
        console.error('Failed to save recording:', e);
        setRecordingModalOpen(false);
      } finally {
        savingRef.current = false;
      }
    } else {
      transcriptRef.current = '';
      dismissAgent();
      startTranscription();
      setRecordingModalOpen(true);
    }
  };

  const handleRecordingStop = async () => {
    if (isRecording) {
      handleMicToggle(); // normal stop flow
    } else {
      // Clicked stop before WebSocket connected — just cancel
      await stopTranscription().catch(() => {});
      setRecordingModalOpen(false);
    }
  };

  const handleAgentDismiss = () => {
    dismissAgent();
    setRecordingModalOpen(false);
  };

  const handleFollowUp = () => {
    transcriptRef.current = '';
    dismissAgent();
    startTranscription();
    // Modal already open — recording resumes
  };

  useEffect(() => {
    setRemoteToggle(handleMicToggle);
  });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const checkPending = async () => {
      try {
        const result = await NativeRecorder.getPendingRecording();
        if (result.path) {
          console.log('[Recording] Found pending background recording:', result.path);
          await api.createRecording({ transcript: '[Watch recording - audio saved]' });
          setRecordingsRefreshKey(k => k + 1);
        }
      } catch (e) {
        console.error('[Recording] Failed to check pending recording:', e);
      }
    };

    checkPending();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkPending();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleSelect = (id: number | null) => {
    setContentVisible(false);
    setTimeout(() => {
      setSelectedId(id);
      setDisplayedId(id);
      setContentVisible(true);
    }, 300);
  };

  const topIcons = [
    { Icon: SettingsIcon, label: 'Settings' },
    { Icon: HeadphonesIcon, label: 'Recordings' },
    { Icon: PersonIcon, label: 'Patients' },
  ];
  const [activeIcon, setActiveIcon] = useState(topIcons.length - 1);
  const containerRef = useRef<HTMLDivElement>(null);
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hillPos, setHillPos] = useState(300);

  const measureIcons = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const containerW = containerRect.width;
    if (containerW === 0) return;
    const iconEl = iconRefs.current[activeIcon];
    if (!iconEl) return;
    const iconRect = iconEl.getBoundingClientRect();
    const iconCenter = iconRect.left + iconRect.width / 2 - containerRect.left;
    setHillPos((iconCenter / containerW) * 400);
  }, [activeIcon]);

  useEffect(() => {
    measureIcons();
    window.addEventListener('resize', measureIcons);
    return () => window.removeEventListener('resize', measureIcons);
  }, [measureIcons]);

  const onboardingRefs: OnboardingRefs = { micRef, patientsIconRef, recordingsIconRef, settingsIconRef, addBtnRef };

  const panels = [
    <SettingsPanel key="settings" selectedDeviceId={selectedDeviceId} onSelectDevice={setSelectedDeviceId} />,
    <RecordingsPanel key="recordings" refreshKey={recordingsRefreshKey} />,
    <PatientsPanel key="patients" onSelect={handleSelect} onAdd={() => setAddOpen(true)} onImport={() => setImportOpen(true)} refreshKey={refreshKey} />,
  ];

  return (
    <Box sx={{ mx: 'auto', px: 0.5, pb: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh', justifyContent: 'flex-end', maxWidth: 480, width: '100%' }}>
      <Box sx={{ flex: 1, minHeight: 0 }} />
      <Box sx={{ height: 100, flexShrink: 0 }} />
      <Box sx={{ position: 'relative' }} ref={containerRef}>
        <Box
          ref={micRef}
          onClick={handleMicToggle}
          sx={{
            position: 'absolute', top: -50, left: 12, zIndex: 4,
            height: 50, display: 'flex', alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <MicIcon sx={{ fontSize: 24, color: isRecording ? 'error.main' : 'rgba(0,0,0,0.5)', opacity: isRecording ? 1 : 0.5 }} />
        </Box>
        <Stack direction="row" spacing={6} sx={{ justifyContent: 'flex-end', px: 3, position: 'absolute', top: -50, left: 0, width: '100%', zIndex: 3, height: 50, alignItems: 'center', pointerEvents: 'none' }}>
          {topIcons.map(({ Icon, label }, i) => (
            <Box
              key={label}
              ref={(el: HTMLDivElement | null) => {
                iconRefs.current[i] = el;
                if (i === 0) (settingsIconRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                if (i === 1) (recordingsIconRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                if (i === 2) (patientsIconRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }}
              onClick={() => setActiveIcon(i)}
              sx={{ cursor: 'pointer', opacity: activeIcon === i ? 1 : 0.4, transition: 'opacity 0.2s', pointerEvents: 'auto' }}
            >
              <Icon sx={{ fontSize: 24, color: activeIcon === i ? '#1e3a5f' : 'rgba(0,0,0,0.5)' }} />
            </Box>
          ))}
        </Stack>
        <HillShape
          position={hillPos}
          width={70}
          height={50}
          bodyHeight={0}
          flatTop={30}
          color="rgba(255, 255, 255, 0.4)"
          sx={{ position: 'absolute', top: -50, left: 0, width: '100%', zIndex: 2 }}
          cssHeight={50}
        />
        <Box sx={{
          bgcolor: 'rgba(255, 255, 255, 0.45)',
          borderRadius: '24px 0 0 0',
          p: 2.5, pb: 0, overflow: 'visible',
          boxShadow: '0 -4px 32px rgba(30,58,95,0.08)',
          display: 'flex', flexDirection: 'column',
          height: 'calc(85vh + 68px + 20px)',
        }}>
          <Box sx={{ height: 68, mb: '20px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {activeIcon === 2 ? (
              <Stack direction="row" spacing={1.5} sx={{ width: '100%', alignItems: 'stretch' }}>
                <Box
                  ref={addBtnRef}
                  onClick={selectedId ? () => handleSelect(null) : () => setAddOpen(true)}
                  sx={{
                    width: 48, height: 48, alignSelf: 'center',
                    bgcolor: 'transparent', borderRadius: '50%', border: '1.5px solid rgba(0,0,0,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  {selectedId ? (
                    <ArrowBackIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.4)' }} />
                  ) : (
                    <AddIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.4)' }} />
                  )}
                </Box>
                {/* Create bar — hidden for now
                <Box sx={{
                  flex: 1, minHeight: 48, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2,
                }}>
                  <Typography sx={{ fontWeight: 500, color: glass.text, ml: 1 }}>Create</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {[
                      { Icon: CalendarTodayIcon, action: undefined },
                      { Icon: NoteAddIcon, action: displayedId ? () => setShowNewEncounter(true) : undefined },
                      { Icon: LocalPhoneIcon, action: undefined },
                      { Icon: EmailIcon, action: undefined },
                    ].map(({ Icon, action }, idx) => (
                      <Box key={idx} onClick={action} sx={{
                        width: 36, height: 36, borderRadius: '50%', border: '1.5px solid rgba(0,0,0,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: action ? 'pointer' : 'default',
                        '&:hover': action ? { bgcolor: 'rgba(0,0,0,0.04)' } : {},
                      }}>
                        <Icon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.4)' }} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
                */}
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1.5rem', color: glass.text, px: 1 }}>
                {topIcons[activeIcon].label}
              </Typography>
            )}
          </Box>
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            transition: 'opacity 300ms',
            opacity: contentVisible ? 1 : 0,
          }}>
            {displayedId && activeIcon === 2 ? (
              <PatientDetail patientId={displayedId} />
            ) : (
              panels[activeIcon]
            )}
          </Box>
        </Box>
      </Box>

      <AddPatientDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={() => setRefreshKey(k => k + 1)} />
      <RecordingModal
        open={recordingModalOpen}
        transcript={transcriptRef.current}
        partial={partialText}
        agentState={agentState}
        isRecording={isRecording}
        onStop={handleRecordingStop}
        onDismiss={handleAgentDismiss}
        onFollowUp={handleFollowUp}
      />
      <OnboardingOverlay refs={onboardingRefs} />
      {/* <ImportPatientsDialog open={importOpen} onClose={() => setImportOpen(false)} onSuccess={() => setRefreshKey(k => k + 1)} /> */}
    </Box>
  );
}
